// frontend/src/pages/jobs/JobCreate.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Select,
  Space,
  message,
  Row,
  Col,
  Typography,
  DatePicker,
  InputNumber,
  Spin,
  Divider,
  AutoComplete,
} from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  PhoneOutlined,
  MailOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { trimFormValues } from '../../utils/trimFormValues';
dayjs.extend(utc);

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const JobCreate = () => {
  const [form] = Form.useForm();
  const [drivers, setDrivers] = useState([]);
  const [vans, setVans] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  const [plPosition, setPlPosition] = useState('income');
  const [relatedJobs, setRelatedJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cloneJobId = searchParams.get('clone');
  const hasClonedRef = useRef(false);
  const clonedJobIdRef = useRef(null);

  useEffect(() => {
    // Reset clone tracking if cloneJobId changes
    if (cloneJobId !== clonedJobIdRef.current) {
      hasClonedRef.current = false;
      clonedJobIdRef.current = cloneJobId;
    }

    const initializeForm = async () => {
      try {
        setFormLoading(true);
        
        let currentPlPosition = plPosition;
        let jobToClone = null;
        
        // If cloning and we haven't cloned this job yet, fetch the job data
        if (cloneJobId && !hasClonedRef.current) {
          const jobRes = await apiService.jobs.getById(cloneJobId);
          jobToClone = jobRes.data;
          currentPlPosition = jobToClone.plPosition || 'income';
          setPlPosition(currentPlPosition);
        }
        
        // Fetch initial data with the correct plPosition
        await fetchInitialData(currentPlPosition);
        
        // Fetch all unique customers for the customer field
        const customersRes = await apiService.customers.getUnique();
        setCustomers(customersRes.data || []);

        // If cloning and we haven't populated the form yet, populate it
        if (cloneJobId && jobToClone && !hasClonedRef.current) {
          const job = jobToClone;
          
          // Mark as cloned to prevent duplicate execution
          hasClonedRef.current = true;

          // Generate new references
          const timestamp = new Date().getTime();
          const randomNum = Math.floor(Math.random() * 1000);
          const newJobReference = `JOB-${timestamp}-${randomNum}`;
          
          // Get new invoice number for payment
          const invoiceRes = await apiService.jobs.generateInvoiceNumber();
          const newPaymentReference = invoiceRes.data.reference;

          // Prepare cloned job data (exclude IDs, references, and relationships)
          const clonedData = {
            reference: newJobReference,
            date: job.date ? dayjs(job.date) : dayjs(),
            plPosition: currentPlPosition,
            plDescription: job.plDescription || '',
            bookingReference: job.bookingReference || '',
            channel: typeof job.channel === 'object' ? job.channel.name : job.channel || '',
            jobRequirement: job.jobRequirement || 'one-man',
            jobStatus: 'unassigned', // Reset status for cloned job
            jobNotes: job.jobNotes || '',
            customer: job.customer ? {
              name: job.customer.name || '',
              email: job.customer.email || '',
              phone: job.customer.phone || '',
            } : { name: '', email: '', phone: '' },
            collectionDetails: {
              date: job.collectionDetails?.date ? dayjs(job.collectionDetails.date) : null,
              time: job.collectionDetails?.time || '',
              address: job.collectionDetails?.address || '',
              postcode: job.collectionDetails?.postcode || '',
            },
            delivery: {
              date: job.delivery?.date ? dayjs(job.delivery.date) : null,
              time: job.delivery?.time || '',
              address: job.delivery?.address || '',
              postcode: job.delivery?.postcode || '',
            },
            // Don't copy driver, van, or relatedJob - let user assign fresh
            payment: job.payment ? {
              reference: newPaymentReference,
              amount: job.payment.amount || 0,
              vatRate: job.payment.vatRate || 20,
              vatAmount: job.payment.vatAmount || 0,
              totalAmount: job.payment.totalAmount || 0,
              status: 'pending', // Reset payment status
              paymentMethod: job.payment.paymentMethod || 'bank_transfer',
              dueDate: job.payment.dueDate ? dayjs(job.payment.dueDate) : null,
              notes: job.payment.description || job.payment.notes || '',
            } : {
              reference: newPaymentReference,
              vatRate: 20,
              status: 'pending',
              paymentMethod: 'bank_transfer'
            }
          };

          // Set form values
          form.setFieldsValue(clonedData);

          message.success('Job cloned successfully. Please review and modify as needed before creating.');
        }
      } catch (error) {
        console.error('Error initializing form:', error);
        if (cloneJobId) {
          message.error('Failed to clone job. Please try again.');
          navigate('/jobs');
        } else {
          message.error('Failed to load initial data');
        }
      } finally {
        setFormLoading(false);
      }
    };

    initializeForm();
  }, [cloneJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInitialData = async (plPos = plPosition) => {
    const [driversRes, vansRes, jobsRes, channelsRes, invoiceRes] = await Promise.all([
      apiService.drivers.getAll(),
      apiService.vans.getAll(),
      apiService.jobs.getAllForDropdown({ plPosition: plPos === 'income' ? 'expense' : 'income' }),
      apiService.channels.getActive(),
      apiService.jobs.generateInvoiceNumber()
    ]);

    setDrivers(driversRes.data);
    setVans(vansRes.data);
    setChannels(channelsRes.data);
    
    // Filter jobs that don't have a related job
    const jobsArray = Array.isArray(jobsRes.data) ? jobsRes.data : [];
    const filteredJobs = jobsArray.filter(job => !job.relatedJob);
    setRelatedJobs(filteredJobs);

    // Only set initial form values if not cloning
    if (!cloneJobId) {
      const timestamp = new Date().getTime();
      const randomNum = Math.floor(Math.random() * 1000);
      
      form.setFieldsValue({
        reference: `JOB-${timestamp}-${randomNum}`,
        date: dayjs(),
        jobStatus: 'unassigned',
        payment: {
          reference: invoiceRes.data.reference,
          vatRate: 20,
          status: 'pending',
          paymentMethod: 'bank_transfer'
        }
      });
    }
  };

  const calculateVAT = (netAmount, vatRate) => {
    return (netAmount * vatRate) / 100;
  };

  const calculateTotal = (netAmount, vatAmount) => {
    return netAmount + vatAmount;
  };

  const handleAmountChange = (value) => {
    // Only calculate if value exists and is not 0
    if (value && value > 0) {
      const vatRate = form.getFieldValue(['payment', 'vatRate']);
      // Only calculate if we have a valid VAT rate
      if (vatRate !== undefined && vatRate !== null) {
        const vatAmount = calculateVAT(value, vatRate);
        const totalAmount = calculateTotal(value, vatAmount);
        
        form.setFieldsValue({
          payment: {
            vatAmount: vatAmount,
            totalAmount: totalAmount
          }
        });
      }
    } else {
      // Reset VAT and total amounts if amount is 0 or cleared
      form.setFieldsValue({
        payment: {
          vatAmount: 0,
          totalAmount: 0
        }
      });
    }
  };

  const handleVatRateChange = (value) => {
    const amount = form.getFieldValue(['payment', 'amount']);
    // Only calculate if amount exists and is not 0
    if (amount && amount > 0) {
      const vatAmount = calculateVAT(amount, value);
      const totalAmount = calculateTotal(amount, vatAmount);
      
      form.setFieldsValue({
        payment: {
          vatAmount: vatAmount,
          totalAmount: totalAmount
        }
      });
    } else {
      // Reset VAT and total amounts if no amount is entered
      form.setFieldsValue({
        payment: {
          vatAmount: 0,
          totalAmount: 0
        }
      });
    }
  };

  const handlePlPositionChange = async (value) => {
    setPlPosition(value);
    try {
      const [response, jobsResponse] = await Promise.all([
        apiService.jobs.generateInvoiceNumber(),
        apiService.jobs.getAllForDropdown({ plPosition: value === 'income' ? 'expense' : 'income' })
      ]);
      
      form.setFieldsValue({
        payment: {
          ...form.getFieldValue('payment'),
          reference: response.data.reference
        }
      });
      
      // Filter jobs that don't have a related job
      const jobsArray = Array.isArray(jobsResponse.data) ? jobsResponse.data : [];
      const filteredJobs = jobsArray.filter(job => !job.relatedJob);
      setRelatedJobs(filteredJobs);
    } catch (error) {
      console.error('Error updating form:', error);
      message.error('Failed to update form');
    }
  };

  const handleRelatedJobChange = async (jobId) => {
    if (!jobId) return;
    try {
      const res = await apiService.jobs.getById(jobId);
      const job = res.data;
      // Prepare fields to copy (exclude payment fields)
      const fieldsToCopy = {
        customer: job.customer
          ? {
              name: job.customer.name || '',
              email: job.customer.email || '',
              phone: job.customer.phone || '',
            }
          : { name: '', email: '', phone: '' },
        van: job.van?._id || job.van || undefined,
        driver: job.driver?._id || job.driver || undefined,
        channel:
          typeof job.channel === 'object'
            ? job.channel.name
            : job.channel || undefined,
        date: job.date ? dayjs(job.date) : null,
        jobStatus: job.jobStatus,
        bookingReference: job.bookingReference,
        plDescription: job.plDescription,
        jobRequirement: job.jobRequirement,
        collectionDetails: {
          ...job.collectionDetails,
          date: job.collectionDetails?.date ? dayjs(job.collectionDetails.date) : null,
        },
        delivery: {
          ...job.delivery,
          date: job.delivery?.date ? dayjs(job.delivery.date) : null,
        },
      };
      const currentPayment = form.getFieldValue('payment') || {};
      form.setFieldsValue({
        ...fieldsToCopy,
        payment: {
          ...currentPayment,
          reference: currentPayment.reference || '',
        }
      });
    } catch (err) {
      message.error('Failed to fetch related job details');
    }
  };

  const handleSubmit = async (values) => {
    const trimmedValues = trimFormValues(values);
    try {
      setLoading(true);
      
      // Automatically set status to "assigned" if a driver is selected
      const jobStatus = trimmedValues.driver ? 'assigned' : trimmedValues.jobStatus || 'unassigned';

      // Format dates and ensure all required fields are present
      const formattedValues = {
        ...trimmedValues,
        date: trimmedValues.date.format('YYYY-MM-DD') + 'T00:00:00.000Z',
        plPosition: plPosition,
        jobRequirement: trimmedValues.jobRequirement || 'one-man',
        jobStatus: jobStatus, // Use the determined job status
        collectionDetails: {
          ...trimmedValues.collectionDetails,
          date: trimmedValues.collectionDetails.date.format('YYYY-MM-DD') + 'T00:00:00.000Z',
          time: trimmedValues.collectionDetails.time || '09:00'
        },
        delivery: {
          ...trimmedValues.delivery,
          date: trimmedValues.delivery.date.format('YYYY-MM-DD') + 'T00:00:00.000Z',
          time: trimmedValues.delivery.time || '17:00'
        },
        payment: trimmedValues.payment ? {
          ...trimmedValues.payment,
          paymentDueDate: trimmedValues.payment.paymentDueDate ? trimmedValues.payment.paymentDueDate.format('YYYY-MM-DD') + 'T00:00:00.000Z' : undefined
        } : undefined
      };

      console.log('Submitting job data:', formattedValues);

      // Create the job
      const jobResponse = await apiService.jobs.create(formattedValues);
      console.log('Job creation response:', jobResponse);

      message.success('Job created successfully');
      navigate('/jobs');
    } catch (error) {
      console.error('Error creating job:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      message.error(error.response?.data?.message || 'Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  if (formLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <CalendarOutlined />
            <span>{cloneJobId ? 'Clone Job - Review & Create' : 'Create New Job'}</span>
          </Space>
        }
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/jobs')}
          >
            Back
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            payment: {
              status: 'pending',
            }
          }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Title level={4}>Job Details</Title>
            </Col>

            <Col span={8}>
              <Form.Item
                name="reference"
                label="Reference"
                rules={[{ required: true, message: 'Please enter reference' }]}
              >
                <Input disabled />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="bookingReference"
                label="External Reference"
                rules={[]}
              >
                <Input placeholder="Enter external/booking reference (optional)" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="date"
                label="Date"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="plPosition"
                label="PL Position"
                rules={[{ required: true, message: 'Please select PL position' }]}
              >
                <Select onChange={handlePlPositionChange}>
                  <Option value="income">Income</Option>
                  <Option value="expense">Expense</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="relatedJob"
                label="Link to Related Job"
                tooltip="Link this job to another job with opposite PL position"
                required={plPosition === 'expense'}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue('plPosition') === 'expense' && !value) {
                        return Promise.reject(new Error('Link to Related Job is required for expense jobs'));
                      }
                      return Promise.resolve();
                    },
                  })
                ]}
              >
                <Select
                  allowClear
                  placeholder="Select related job"
                  showSearch
                  optionFilterProp="children"
                  onChange={handleRelatedJobChange}
                >
                  {relatedJobs.map(job => (
                    <Option key={job._id} value={job._id}>
                      {job.bookingReference || job.reference || 'No Reference'}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="channel"
                label="Channel"
                rules={[{ required: true, message: 'Please select a channel' }]}
              >
                <Select placeholder="Select channel">
                  {channels.map(channel => (
                    <Option key={channel._id || channel.name} value={channel.name}>
                      {channel.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="jobRequirement"
                label="Job Requirement"
                rules={[{ required: true, message: 'Please select job requirement' }]}
              >
                <Select>
                  <Option value="one-man">One Man</Option>
                  <Option value="two-man">Two Man</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="jobStatus"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select>
                  <Option value="unassigned">Unassigned</Option>
                  <Option value="assigned">Assigned</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="driver"
                label="Driver"
                tooltip="Assign a driver to this job"
              >
                <Select
                  allowClear
                  placeholder="Select driver"
                  showSearch
                  optionFilterProp="children"
                >
                  {drivers.map(driver => (
                    <Option key={driver._id} value={driver._id}>
                      {driver.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="van"
                label="Vehicle"
                tooltip="Assign a vehicle to this job"
              >
                <Select
                  allowClear
                  placeholder="Select vehicle"
                  showSearch
                  optionFilterProp="children"
                >
                  {vans.map(van => (
                    <Option key={van._id} value={van._id}>
                      {van.registration} ({van.make} {van.model})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Divider>Customer Details</Divider>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['customer', 'name']}
                label="Customer"
                rules={[{ required: true, message: 'Please enter or select a customer name' }]}
              >
                <AutoComplete
                  placeholder="Enter or select customer name"
                  filterOption={(inputValue, option) =>
                    option.value.toLowerCase().includes(inputValue.toLowerCase())
                  }
                  onSelect={(value) => {
                    form.setFieldsValue({
                      customer: {
                        ...form.getFieldValue('customer'),
                        name: value
                      }
                    });
                  }}
                  onChange={(value) => {
                    form.setFieldsValue({
                      customer: {
                        ...form.getFieldValue('customer'),
                        name: value
                      }
                    });
                  }}
                  options={customers.map(customer => ({ value: customer.name }))}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['customer', 'email']}
                label="Email"
                rules={[
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input prefix={<MailOutlined />} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['customer', 'phone']}
                label="Phone"
                rules={[{ required: true, message: 'Please enter phone number' }]}
              >
                <Input prefix={<PhoneOutlined />} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Divider>Collection Details</Divider>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['collectionDetails', 'date']}
                label="Collection Date"
                rules={[{ required: true, message: 'Please select collection date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['collectionDetails', 'time']}
                label="Collection Time"
                rules={[{ required: true, message: 'Please enter collection time' }]}
              >
                <Input placeholder="e.g. 09:00" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['collectionDetails', 'postcode']}
                label="Collection Postcode"
                rules={[{ required: true, message: 'Please enter collection postcode' }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name={['collectionDetails', 'address']}
                label="Collection Address"
                rules={[{ required: true, message: 'Please enter collection address' }]}
              >
                <TextArea rows={2} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Divider>Delivery Details</Divider>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['delivery', 'date']}
                label="Delivery Date"
                rules={[{ required: true, message: 'Please select delivery date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['delivery', 'time']}
                label="Delivery Time"
                rules={[{ required: true, message: 'Please enter delivery time' }]}
              >
                <Input placeholder="e.g. 17:00" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['delivery', 'postcode']}
                label="Delivery Postcode"
                rules={[{ required: true, message: 'Please enter delivery postcode' }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name={['delivery', 'address']}
                label="Delivery Address"
                rules={[{ required: true, message: 'Please enter delivery address' }]}
              >
                <TextArea rows={2} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Title level={4}>Payment Details</Title>
            </Col>

            <Col span={12}>
              <Form.Item
                name={['payment', 'reference']}
                label="Reference Number"
              >
                <Input disabled />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name={['payment', 'dueDate']}
                label="Payment Due Date"
                rules={[{ required: true, message: 'Please select payment due date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['payment', 'amount']}
                label="Amount (£)"
                rules={[{ required: true, message: 'Please enter amount' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  onChange={handleAmountChange}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['payment', 'vatRate']}
                label="VAT Rate (%)"
                rules={[{ required: true, message: 'Please enter VAT rate' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  step={0.1}
                  precision={1}
                  onChange={handleVatRateChange}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['payment', 'vatAmount']}
                label="VAT Amount (£)"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  disabled
                  precision={2}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['payment', 'totalAmount']}
                label="Total Amount (£)"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  disabled
                  precision={2}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['payment', 'status']}
                label="Payment Status"
                rules={[{ required: true, message: 'Please select payment status' }]}
              >
                <Select>
                  <Option value="pending">Pending</Option>
                  <Option value="paid">Paid</Option>
                  <Option value="overdue">Overdue</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name={['payment', 'paymentMethod']}
                label="Payment Method"
                rules={[{ required: true, message: 'Please select payment method' }]}
              >
                <Select>
                  <Option value="bank_transfer">Bank Transfer</Option>
                  <Option value="credit_card">Credit Card</Option>
                  <Option value="debit_card">Debit Card</Option>
                  <Option value="cash">Cash</Option>
                  <Option value="check">Check</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name={['payment', 'notes']}
                label="Payment Notes"
              >
                <TextArea rows={2} />
              </Form.Item>
            </Col>

            <Col span={24} style={{ textAlign: 'right', marginTop: 16 }}>
              <Button type="primary" htmlType="submit" loading={loading}>
                Create Job
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default JobCreate;